import { createElement, useCallback, useMemo, useRef } from 'react';

import {
  GridGroupingColDefOverride,
  GridRenderCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';

import GeneratikGroupingCell from '../GeneratikGroupingCell';
import { GeneratikTableColumn } from '../GeneratikTable.types';
import { resolveGroupValue } from '../GeneratikTable.utils';

const defaultRenderGroupingCell = (params: GridRenderCellParams) =>
  createElement(GeneratikGroupingCell, params);

export interface UseTableGroupingParams<R extends GridValidRowModel> {
  groupByField?: string | null;
  treeData?: boolean;
  columns: GeneratikTableColumn<R>[];
  rows: R[];
  getRowId: (row: R) => string | number;
  userGetTreeDataPath?: (row: R) => Array<string | number>;
  groupValueGetter?: (row: R, field: string) => string | number | null | undefined;
  renderGroupingCell?: (params: GridRenderCellParams) => React.ReactNode;
  groupingColumnWidth?: number;
  onGroupChange?: (field: string | null) => void;
}

export interface UseTableGroupingReturn<R extends GridValidRowModel> {
  treeDataEnabled: boolean;
  effectiveGetTreeDataPath: ((row: R) => Array<string | number>) | undefined;
  groupingColDef: GridGroupingColDefOverride<R>;
  handleGroupChange: (field: string | null) => void;
  isDegenerateGrouping: boolean;
}

const useTableGrouping = <R extends GridValidRowModel>({
  groupByField,
  treeData,
  columns,
  rows,
  getRowId,
  userGetTreeDataPath,
  groupValueGetter,
  renderGroupingCell,
  groupingColumnWidth = 48,
  onGroupChange,
}: UseTableGroupingParams<R>): UseTableGroupingReturn<R> => {
  const treeDataEnabled = useMemo(() => {
    if (treeData === false) return false;
    if (treeData === true) return true;
    return !!(groupByField || userGetTreeDataPath);
  }, [treeData, groupByField, userGetTreeDataPath]);

  const rootGroupValueMapRef = useRef<Map<string, string>>(new Map());
  const rootGroupValueMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!groupByField) {
      rootGroupValueMapRef.current = map;
      return map;
    }
    for (const row of rows) {
      const userPath = userGetTreeDataPath ? userGetTreeDataPath(row) : [getRowId(row)];
      if (userPath.length === 1) {
        map.set(
          String(userPath[0]),
          resolveGroupValue(row, groupByField, columns, groupValueGetter),
        );
      }
    }
    rootGroupValueMapRef.current = map;
    return map;
  }, [groupByField, rows, userGetTreeDataPath, getRowId, columns, groupValueGetter]);

  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const groupValueGetterRef = useRef(groupValueGetter);
  groupValueGetterRef.current = groupValueGetter;
  const userGetTreeDataPathRef = useRef(userGetTreeDataPath);
  userGetTreeDataPathRef.current = userGetTreeDataPath;
  const getRowIdRef = useRef(getRowId);
  getRowIdRef.current = getRowId;
  const groupByFieldRef = useRef(groupByField);
  groupByFieldRef.current = groupByField;

  const effectiveGetTreeDataPath = useMemo(() => {
    if (!treeDataEnabled) return undefined;
    return (row: R): Array<string | number> => {
      const userPath = userGetTreeDataPathRef.current
        ? userGetTreeDataPathRef.current(row)
        : [getRowIdRef.current(row)];

      const field = groupByFieldRef.current;
      if (!field) return userPath;

      if (userPath.length === 1) {
        return [
          resolveGroupValue(row, field, columnsRef.current, groupValueGetterRef.current),
          ...userPath,
        ];
      }

      const parentKey = String(userPath[0]);
      const parentGroupValue =
        rootGroupValueMapRef.current.get(parentKey) ??
        resolveGroupValue(row, field, columnsRef.current, groupValueGetterRef.current);
      return [parentGroupValue, ...userPath];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeDataEnabled, groupByField]);

  // Touch the map dependency so the memoizer revalidates the closure when grouping data shifts.
  // The function body itself reads from `rootGroupValueMapRef`, so its reference stays stable.
  void rootGroupValueMap;

  const isDegenerateGrouping = useMemo(() => {
    if (!groupByField || rows.length === 0) return false;
    const firstKey = resolveGroupValue(rows[0], groupByField, columns, groupValueGetter);
    return rows.every(
      (row) => resolveGroupValue(row, groupByField, columns, groupValueGetter) === firstKey,
    );
  }, [groupByField, rows, columns, groupValueGetter]);

  const handleGroupChange = useCallback(
    (field: string | null) => {
      onGroupChange?.(field);
    },
    [onGroupChange],
  );

  const groupingColDef = useMemo<GridGroupingColDefOverride<R>>(
    () => ({
      disableReorder: true,
      hideDescendantCount: true,
      headerName: '',
      width: groupingColumnWidth,
      minWidth: groupingColumnWidth,
      maxWidth: groupingColumnWidth,
      resizable: false,
      renderCell: renderGroupingCell ?? defaultRenderGroupingCell,
    }),
    [groupingColumnWidth, renderGroupingCell],
  );

  return {
    treeDataEnabled,
    effectiveGetTreeDataPath,
    groupingColDef,
    handleGroupChange,
    isDegenerateGrouping,
  };
};

export default useTableGrouping;
